/**
 * Legal Reference Detection and Linking Utilities
 * Detects Spanish legal references and converts them to clickable links
 * Expanded dictionary covering ~180+ administrative law norms
 * 
 * Matching strategy:
 * 1. Full law names (case-insensitive) - safe because they are long and unambiguous
 * 2. Abbreviations (CASE-SENSITIVE, uppercase only) - prevents false positives
 * 3. Numeric references (Ley X/YYYY, RD X/YYYY) - inherently safe
 */

export interface LegalReference {
  type: 'boe' | 'law' | 'court' | 'eu' | 'doe' | 'boja';
  original: string;
  url: string;
}

// ============================================================
// ABBREVIATION → URL MAPPING (keys MUST be uppercase)
// Only matched when text is in UPPERCASE (case-sensitive)
// ============================================================
const abbreviationMapping: Record<string, string> = {
  // === CONSTITUCIÓN Y CÓDIGOS FUNDAMENTALES ===
  'CE': 'https://www.boe.es/eli/es/c/1978/12/27/(1)/con',
  'CC': 'https://www.boe.es/eli/es/rd/1889/07/24/(1)/con',
  'CP': 'https://www.boe.es/eli/es/lo/1995/11/23/10/con',

  // === PROCEDIMIENTO ADMINISTRATIVO ===
  'LPAC': 'https://www.boe.es/eli/es/l/2015/10/01/39/con',
  'LRJSP': 'https://www.boe.es/eli/es/l/2015/10/01/40/con',
  'LRJPAC': 'https://www.boe.es/eli/es/l/1992/11/26/30/con',
  'LPA': 'https://www.boe.es/eli/es/l/1958/07/17/(1)/con',
  'LPACAP': 'https://www.boe.es/eli/es/l/2015/10/01/39/con',

  // === RÉGIMEN LOCAL ===
  'LBRL': 'https://www.boe.es/eli/es/l/1985/04/02/7/con',
  'TRRL': 'https://www.boe.es/eli/es/rdlg/1986/04/18/781/con',
  'ROF': 'https://www.boe.es/eli/es/rd/1986/11/28/2568/con',
  'RSCL': 'https://www.boe.es/eli/es/d/1955/06/17/(1)/con',
  'RBEL': 'https://www.boe.es/eli/es/rd/1986/06/13/1372/con',
  'RPDT': 'https://www.boe.es/eli/es/rd/1986/07/11/1690/con',
  'LRSAL': 'https://www.boe.es/eli/es/l/2013/12/27/27/con',

  // === HACIENDAS LOCALES Y TRIBUTARIO ===
  'TRLRHL': 'https://www.boe.es/eli/es/rdlg/2004/03/05/2/con',
  'LHL': 'https://www.boe.es/eli/es/rdlg/2004/03/05/2/con',
  'LGT': 'https://www.boe.es/eli/es/l/2003/12/17/58/con',
  'LGP': 'https://www.boe.es/eli/es/l/2003/11/26/47/con',
  'LOEPSF': 'https://www.boe.es/eli/es/lo/2012/04/27/2/con',
  'LOFCA': 'https://www.boe.es/eli/es/lo/1980/09/22/8/con',

  // === CONTABILIDAD PÚBLICA ===
  'ICAL': 'https://www.boe.es/eli/es/o/2004/11/23/eha4041/con',
  'PGCP': 'https://www.boe.es/eli/es/o/2010/12/28/ehd3067/con',

  // === EMPLEO PÚBLICO ===
  'TREBEP': 'https://www.boe.es/eli/es/rdlg/2015/10/30/5/con',
  'EBEP': 'https://www.boe.es/eli/es/rdlg/2015/10/30/5/con',
  'LMRFP': 'https://www.boe.es/eli/es/l/1984/08/02/30/con',
  'RGS': 'https://www.boe.es/eli/es/rd/1995/03/10/364/con',

  // === CONTRATACIÓN PÚBLICA ===
  'LCSP': 'https://www.boe.es/eli/es/l/2017/11/08/9/con',
  'RGLCAP': 'https://www.boe.es/eli/es/rd/2001/10/12/1098/con',
  'TRLCSP': 'https://www.boe.es/eli/es/rdlg/2011/11/14/3/con',
  'LCAP': 'https://www.boe.es/eli/es/l/1995/05/18/13/con',
  'LPGE': 'https://www.boe.es/eli/es/l/2017/06/27/3/con',

  // === SEGURIDAD CIUDADANA Y POLICÍA ===
  'LOPSC': 'https://www.boe.es/eli/es/lo/2015/03/30/4/con',
  'LOFCS': 'https://www.boe.es/eli/es/lo/1986/03/13/2/con',
  'LODN': 'https://www.boe.es/eli/es/lo/2005/11/17/5/con',
  'LTSV': 'https://www.boe.es/eli/es/rdlg/2015/10/30/6/con',
  'RGT': 'https://www.boe.es/eli/es/rd/2003/11/21/1428/con',

  // === URBANISMO Y SUELO ===
  'TRLS': 'https://www.boe.es/eli/es/rdlg/2015/10/30/7/con',
  'LEF': 'https://www.boe.es/eli/es/l/1954/12/16/(1)/con',
  'REF': 'https://www.boe.es/eli/es/d/1957/04/26/(1)/con',
  'RUC': 'https://www.boe.es/eli/es/rd/1978/06/23/1346/con',

  // === PATRIMONIO ===
  'LPAP': 'https://www.boe.es/eli/es/l/2003/11/03/33/con',
  'LPHE': 'https://www.boe.es/eli/es/l/1985/06/25/16/con',

  // === MEDIO AMBIENTE ===
  'LEA': 'https://www.boe.es/eli/es/l/2013/12/09/21/con',
  'LRSC': 'https://www.boe.es/eli/es/l/2022/04/08/7/con',
  'TRLA': 'https://www.boe.es/eli/es/rdlg/2001/07/20/1/con',
  'LPCIC': 'https://www.boe.es/eli/es/rdlg/2016/12/16/1/con',
  'LCC': 'https://www.boe.es/eli/es/l/2021/05/20/7/con',
  'LRM': 'https://www.boe.es/eli/es/l/2007/10/23/26/con',

  // === TRANSPARENCIA Y PROTECCIÓN DE DATOS ===
  'LTAIBG': 'https://www.boe.es/eli/es/l/2013/12/09/19/con',
  'LOPDGDD': 'https://www.boe.es/eli/es/lo/2018/12/05/3/con',
  'LOPD': 'https://www.boe.es/eli/es/lo/2018/12/05/3/con',
  'RGPD': 'https://eur-lex.europa.eu/eli/reg/2016/679/oj/spa',
  'LAE': 'https://www.boe.es/eli/es/l/2007/06/22/11/con',

  // === SUBVENCIONES ===
  'LGS': 'https://www.boe.es/eli/es/l/2003/11/17/38/con',
  'RLGS': 'https://www.boe.es/eli/es/rd/2006/07/21/887/con',

  // === SERVICIOS SOCIALES Y DEPENDENCIA ===
  'LAPAD': 'https://www.boe.es/eli/es/l/2006/12/14/39/con',
  'LGSS': 'https://www.boe.es/eli/es/rdlg/2015/10/30/8/con',
  'LGDPD': 'https://www.boe.es/eli/es/rdlg/2013/11/29/1/con',
  'LGSAN': 'https://www.boe.es/eli/es/l/1986/04/25/14/con',

  // === JURISDICCIONAL Y PROCESAL ===
  'LJCA': 'https://www.boe.es/eli/es/l/1998/07/13/29/con',
  'LOPJ': 'https://www.boe.es/eli/es/lo/1985/07/01/6/con',
  'LEC': 'https://www.boe.es/eli/es/l/2000/01/07/1/con',
  'LECrim': 'https://www.boe.es/eli/es/rd/1882/09/14/(1)/con',
  'LECr': 'https://www.boe.es/eli/es/rd/1882/09/14/(1)/con',
  'LOTC': 'https://www.boe.es/eli/es/lo/1979/10/03/2/con',
  'LRJS': 'https://www.boe.es/eli/es/l/2011/10/10/36/con',

  // === NORMATIVA EUROPEA ===
  'TFUE': 'https://eur-lex.europa.eu/eli/treaty/tfue_2016/oj/spa',
  'CDFUE': 'https://eur-lex.europa.eu/eli/treaty/char_2016/oj/spa',

  // === OTRAS LEYES RELEVANTES ===
  'LAJ': 'https://www.boe.es/eli/es/l/2003/12/23/62/con',
  'LAU': 'https://www.boe.es/eli/es/l/1994/11/24/29/con',
  'LPH': 'https://www.boe.es/eli/es/l/1960/07/21/49/con',
  'LET': 'https://www.boe.es/eli/es/rdlg/2015/10/23/2/con',
  'LISOS': 'https://www.boe.es/eli/es/rdlg/2000/08/04/5/con',
  'eIDAS': 'https://eur-lex.europa.eu/eli/reg/2014/910/oj/spa',

  // === NORMATIVA AUTONÓMICA DE EXTREMADURA ===
  'LOTUS': 'https://doe.juntaex.es/pdfs/doe/2018/1230o/18010007.pdf',
  'LFPEx': 'https://doe.juntaex.es/pdfs/doe/2015/760o/15010003.pdf',
  'LALEx': 'https://doe.juntaex.es/pdfs/doe/2010/630o/10010009.pdf',
  'LTEx': 'https://doe.juntaex.es/pdfs/doe/2013/480o/13010003.pdf',
  'LSAEx': 'https://doe.juntaex.es/pdfs/doe/2019/440o/19010014.pdf',
  'LCPLEx': 'https://doe.juntaex.es/pdfs/doe/2015/760o/15010004.pdf',
  'LHPEx': 'https://doe.juntaex.es/pdfs/doe/2007/140o/07010002.pdf',
  'LEAEx': 'https://doe.juntaex.es/pdfs/doe/2015/540o/15010006.pdf',
  'LPEx': 'https://doe.juntaex.es/pdfs/doe/2014/1330o/14010008.pdf',
  'LSEx': 'https://doe.juntaex.es/pdfs/doe/2011/820o/11010006.pdf',
  'LTurEx': 'https://doe.juntaex.es/pdfs/doe/2011/880o/11010008.pdf',
  'LCDEx': 'https://doe.juntaex.es/pdfs/doe/2012/2480o/12010013.pdf',

  // === NORMATIVA AUTONÓMICA DE ANDALUCÍA ===
  'LAULA': 'https://www.boe.es/eli/es/l/2010/06/11/5/con',
  'LISTA': 'https://www.juntadeandalucia.es/boja/2021/233/1',
  'LOUA': 'https://www.boe.es/eli/es/l/2002/12/17/7/con',
  'LFPAnd': 'https://www.juntadeandalucia.es/boja/2024/86/1',
  'EAAnd': 'https://www.boe.es/eli/es/lo/2007/03/19/2/con',
  'LHPAnd': 'https://www.juntadeandalucia.es/boja/2010/230/1',
  'LGAnd': 'https://www.boe.es/eli/es/l/2006/10/24/6/con',
  'LPAAnd': 'https://www.boe.es/eli/es/l/2007/10/26/9/con',
  'LSAnd': 'https://www.boe.es/eli/es/l/1998/06/15/2/con',
  'LSSAnd': 'https://www.juntadeandalucia.es/boja/2016/248/1',
  'LTAnd': 'https://www.juntadeandalucia.es/boja/2014/124/1',
  'LPHAnd': 'https://www.boe.es/eli/es/l/2007/11/26/14/con',
  'LEAAnd': 'https://www.boe.es/eli/es/l/2007/07/09/7/con',
  'LPLAnd': 'https://www.boe.es/eli/es/l/2001/12/11/13/con',
  'LSPAnd': 'https://www.boe.es/eli/es/l/2003/05/12/13/con',
  'LDUAnd': 'https://www.juntadeandalucia.es/boja/1999/138/1',
  'LTurAnd': 'https://www.boe.es/eli/es/l/2011/12/23/13/con',
  'LOJAnd': 'https://www.juntadeandalucia.es/boja/2010/145/1',
  'LCPAnd': 'https://www.juntadeandalucia.es/boja/2017/244/1',
};

// ============================================================
// FULL LAW NAMES → URL MAPPING (keys are lowercase, matched case-insensitive)
// Safe because they are long phrases that won't match normal text
// ============================================================
const fullNameMapping: Record<string, string> = {
  // === CONSTITUCIÓN Y CÓDIGOS ===
  'constitución española': 'https://www.boe.es/eli/es/c/1978/12/27/(1)/con',
  'constitución': 'https://www.boe.es/eli/es/c/1978/12/27/(1)/con',
  'código civil': 'https://www.boe.es/eli/es/rd/1889/07/24/(1)/con',
  'código penal': 'https://www.boe.es/eli/es/lo/1995/11/23/10/con',

  // === PROCEDIMIENTO ADMINISTRATIVO ===
  'ley de procedimiento administrativo': 'https://www.boe.es/eli/es/l/2015/10/01/39/con',
  'ley de procedimiento administrativo común': 'https://www.boe.es/eli/es/l/2015/10/01/39/con',
  'ley del régimen jurídico del sector público': 'https://www.boe.es/eli/es/l/2015/10/01/40/con',

  // === RÉGIMEN LOCAL ===
  'ley de bases de régimen local': 'https://www.boe.es/eli/es/l/1985/04/02/7/con',
  'ley de bases del régimen local': 'https://www.boe.es/eli/es/l/1985/04/02/7/con',
  'texto refundido régimen local': 'https://www.boe.es/eli/es/rdlg/1986/04/18/781/con',
  'reglamento de organización y funcionamiento': 'https://www.boe.es/eli/es/rd/1986/11/28/2568/con',
  'reglamento de organización': 'https://www.boe.es/eli/es/rd/1986/11/28/2568/con',
  'reglamento de servicios de las corporaciones locales': 'https://www.boe.es/eli/es/d/1955/06/17/(1)/con',
  'reglamento de servicios': 'https://www.boe.es/eli/es/d/1955/06/17/(1)/con',
  'reglamento de bienes de las entidades locales': 'https://www.boe.es/eli/es/rd/1986/06/13/1372/con',
  'reglamento de bienes': 'https://www.boe.es/eli/es/rd/1986/06/13/1372/con',
  'reglamento de población y demarcación territorial': 'https://www.boe.es/eli/es/rd/1986/07/11/1690/con',
  'reglamento de población': 'https://www.boe.es/eli/es/rd/1986/07/11/1690/con',
  'ley de racionalización': 'https://www.boe.es/eli/es/l/2013/12/27/27/con',
  'ley de racionalización y sostenibilidad': 'https://www.boe.es/eli/es/l/2013/12/27/27/con',

  // === HACIENDAS LOCALES ===
  'texto refundido haciendas locales': 'https://www.boe.es/eli/es/rdlg/2004/03/05/2/con',
  'ley de haciendas locales': 'https://www.boe.es/eli/es/rdlg/2004/03/05/2/con',
  'ley general tributaria': 'https://www.boe.es/eli/es/l/2003/12/17/58/con',
  'ley general presupuestaria': 'https://www.boe.es/eli/es/l/2003/11/26/47/con',
  'ley de estabilidad presupuestaria': 'https://www.boe.es/eli/es/lo/2012/04/27/2/con',
  'instrucción de contabilidad': 'https://www.boe.es/eli/es/o/2004/11/23/eha4041/con',
  'plan general de contabilidad pública': 'https://www.boe.es/eli/es/o/2010/12/28/ehd3067/con',
  'reglamento presupuestario': 'https://www.boe.es/eli/es/rd/1990/04/20/500/con',

  // === EMPLEO PÚBLICO ===
  'estatuto básico del empleado público': 'https://www.boe.es/eli/es/rdlg/2015/10/30/5/con',
  'estatuto del empleado público': 'https://www.boe.es/eli/es/rdlg/2015/10/30/5/con',
  'ley de incompatibilidades': 'https://www.boe.es/eli/es/l/1984/12/26/53/con',
  'ley de medidas para la reforma de la función pública': 'https://www.boe.es/eli/es/l/1984/08/02/30/con',
  'reglamento general de selección': 'https://www.boe.es/eli/es/rd/1995/03/10/364/con',

  // === CONTRATACIÓN PÚBLICA ===
  'ley de contratos del sector público': 'https://www.boe.es/eli/es/l/2017/11/08/9/con',
  'reglamento general de la ley de contratos': 'https://www.boe.es/eli/es/rd/2001/10/12/1098/con',

  // === SEGURIDAD CIUDADANA ===
  'ley de seguridad ciudadana': 'https://www.boe.es/eli/es/lo/2015/03/30/4/con',
  'ley orgánica de seguridad ciudadana': 'https://www.boe.es/eli/es/lo/2015/03/30/4/con',
  'ley de fuerzas y cuerpos de seguridad': 'https://www.boe.es/eli/es/lo/1986/03/13/2/con',
  'ley orgánica de fuerzas y cuerpos de seguridad': 'https://www.boe.es/eli/es/lo/1986/03/13/2/con',
  'ley de defensa nacional': 'https://www.boe.es/eli/es/lo/2005/11/17/5/con',
  'ley de tráfico': 'https://www.boe.es/eli/es/rdlg/2015/10/30/6/con',
  'ley de seguridad vial': 'https://www.boe.es/eli/es/rdlg/2015/10/30/6/con',
  'reglamento general de circulación': 'https://www.boe.es/eli/es/rd/2003/11/21/1428/con',

  // === URBANISMO Y SUELO ===
  'texto refundido de la ley de suelo': 'https://www.boe.es/eli/es/rdlg/2015/10/30/7/con',
  'ley de suelo': 'https://www.boe.es/eli/es/rdlg/2015/10/30/7/con',
  'ley de expropiación forzosa': 'https://www.boe.es/eli/es/l/1954/12/16/(1)/con',
  'reglamento de expropiación forzosa': 'https://www.boe.es/eli/es/d/1957/04/26/(1)/con',
  'ley de costas': 'https://www.boe.es/eli/es/l/1988/07/28/22/con',
  'reglamento de disciplina urbanística': 'https://www.boe.es/eli/es/rd/1978/06/23/1346/con',

  // === PATRIMONIO ===
  'ley de patrimonio de las administraciones públicas': 'https://www.boe.es/eli/es/l/2003/11/03/33/con',
  'ley de montes': 'https://www.boe.es/eli/es/l/2003/11/21/43/con',
  'ley de patrimonio histórico': 'https://www.boe.es/eli/es/l/1985/06/25/16/con',
  'ley de patrimonio histórico español': 'https://www.boe.es/eli/es/l/1985/06/25/16/con',

  // === MEDIO AMBIENTE ===
  'ley de evaluación ambiental': 'https://www.boe.es/eli/es/l/2013/12/09/21/con',
  'ley de residuos': 'https://www.boe.es/eli/es/l/2022/04/08/7/con',
  'ley de residuos y suelos contaminados': 'https://www.boe.es/eli/es/l/2022/04/08/7/con',
  'ley de aguas': 'https://www.boe.es/eli/es/rdlg/2001/07/20/1/con',
  'ley de prevención y control integrados de la contaminación': 'https://www.boe.es/eli/es/rdlg/2016/12/16/1/con',
  'ley de cambio climático': 'https://www.boe.es/eli/es/l/2021/05/20/7/con',
  'ley de responsabilidad medioambiental': 'https://www.boe.es/eli/es/l/2007/10/23/26/con',

  // === TRANSPARENCIA Y PROTECCIÓN DE DATOS ===
  'ley de transparencia': 'https://www.boe.es/eli/es/l/2013/12/09/19/con',
  'ley de protección de datos': 'https://www.boe.es/eli/es/lo/2018/12/05/3/con',
  'reglamento general de protección de datos': 'https://eur-lex.europa.eu/eli/reg/2016/679/oj/spa',
  'ley de acceso electrónico': 'https://www.boe.es/eli/es/l/2007/06/22/11/con',

  // === SUBVENCIONES ===
  'ley general de subvenciones': 'https://www.boe.es/eli/es/l/2003/11/17/38/con',
  'reglamento de la ley general de subvenciones': 'https://www.boe.es/eli/es/rd/2006/07/21/887/con',

  // === SERVICIOS SOCIALES Y DEPENDENCIA ===
  'ley de dependencia': 'https://www.boe.es/eli/es/l/2006/12/14/39/con',
  'ley de promoción de la autonomía personal': 'https://www.boe.es/eli/es/l/2006/12/14/39/con',
  'ley general de la seguridad social': 'https://www.boe.es/eli/es/rdlg/2015/10/30/8/con',
  'ley general de derechos de las personas con discapacidad': 'https://www.boe.es/eli/es/rdlg/2013/11/29/1/con',
  'ley general de sanidad': 'https://www.boe.es/eli/es/l/1986/04/25/14/con',

  // === JURISDICCIONAL Y PROCESAL ===
  'ley de la jurisdicción contencioso-administrativa': 'https://www.boe.es/eli/es/l/1998/07/13/29/con',
  'ley jurisdicción contencioso-administrativa': 'https://www.boe.es/eli/es/l/1998/07/13/29/con',
  'ley orgánica del poder judicial': 'https://www.boe.es/eli/es/lo/1985/07/01/6/con',
  'ley de enjuiciamiento civil': 'https://www.boe.es/eli/es/l/2000/01/07/1/con',
  'ley de enjuiciamiento criminal': 'https://www.boe.es/eli/es/rd/1882/09/14/(1)/con',
  'ley orgánica del tribunal constitucional': 'https://www.boe.es/eli/es/lo/1979/10/03/2/con',
  'ley reguladora de la jurisdicción social': 'https://www.boe.es/eli/es/l/2011/10/10/36/con',

  // === NORMATIVA EUROPEA ===
  'directiva de servicios': 'https://eur-lex.europa.eu/eli/dir/2006/123/oj/spa',
  'directiva bolkestein': 'https://eur-lex.europa.eu/eli/dir/2006/123/oj/spa',
  'reglamento eidas': 'https://eur-lex.europa.eu/eli/reg/2014/910/oj/spa',
  'tratado de funcionamiento': 'https://eur-lex.europa.eu/eli/treaty/tfue_2016/oj/spa',
  'carta de derechos fundamentales': 'https://eur-lex.europa.eu/eli/treaty/char_2016/oj/spa',

  // === OTRAS LEYES ===
  'ley de medidas fiscales': 'https://www.boe.es/eli/es/l/2003/12/23/62/con',
  'ley de arrendamientos urbanos': 'https://www.boe.es/eli/es/l/1994/11/24/29/con',
  'ley de propiedad horizontal': 'https://www.boe.es/eli/es/l/1960/07/21/49/con',
  'estatuto de los trabajadores': 'https://www.boe.es/eli/es/rdlg/2015/10/23/2/con',
  'ley de infracciones y sanciones en el orden social': 'https://www.boe.es/eli/es/rdlg/2000/08/04/5/con',

  // === NORMATIVA AUTONÓMICA DE EXTREMADURA (nombres completos) ===
  'ley de ordenación territorial de extremadura': 'https://doe.juntaex.es/pdfs/doe/2018/1230o/18010007.pdf',
  'ley de función pública de extremadura': 'https://doe.juntaex.es/pdfs/doe/2015/760o/15010003.pdf',
  'ley de administración local de extremadura': 'https://doe.juntaex.es/pdfs/doe/2010/630o/10010009.pdf',
  'ley de gobierno abierto de extremadura': 'https://doe.juntaex.es/pdfs/doe/2013/480o/13010003.pdf',
  'ley de servicios sociales de extremadura': 'https://doe.juntaex.es/pdfs/doe/2019/440o/19010014.pdf',
  'ley de coordinación de policías locales de extremadura': 'https://doe.juntaex.es/pdfs/doe/2015/760o/15010004.pdf',
  'ley de hacienda pública de extremadura': 'https://doe.juntaex.es/pdfs/doe/2007/140o/07010002.pdf',
  'ley de evaluación ambiental de extremadura': 'https://doe.juntaex.es/pdfs/doe/2015/540o/15010006.pdf',
  'ley de patrimonio de extremadura': 'https://doe.juntaex.es/pdfs/doe/2014/1330o/14010008.pdf',
  'ley de salud de extremadura': 'https://doe.juntaex.es/pdfs/doe/2011/820o/11010006.pdf',
  'ley de turismo de extremadura': 'https://doe.juntaex.es/pdfs/doe/2011/880o/11010008.pdf',
  'ley de comercio de extremadura': 'https://doe.juntaex.es/pdfs/doe/2012/2480o/12010013.pdf',

  // === NORMATIVA AUTONÓMICA DE ANDALUCÍA (nombres completos) ===
  'ley de autonomía local de andalucía': 'https://www.boe.es/eli/es/l/2010/06/11/5/con',
  'ley de autonomia local de andalucia': 'https://www.boe.es/eli/es/l/2010/06/11/5/con',
  'ley de impulso para la sostenibilidad del territorio de andalucía': 'https://www.juntadeandalucia.es/boja/2021/233/1',
  'ley de ordenación urbanística de andalucía': 'https://www.boe.es/eli/es/l/2002/12/17/7/con',
  'ley de ordenacion urbanistica de andalucia': 'https://www.boe.es/eli/es/l/2002/12/17/7/con',
  'ley de función pública de andalucía': 'https://www.juntadeandalucia.es/boja/2024/86/1',
  'ley de funcion publica de andalucia': 'https://www.juntadeandalucia.es/boja/2024/86/1',
  'estatuto de autonomía de andalucía': 'https://www.boe.es/eli/es/lo/2007/03/19/2/con',
  'estatuto de autonomia de andalucia': 'https://www.boe.es/eli/es/lo/2007/03/19/2/con',
  'ley de hacienda pública de andalucía': 'https://www.juntadeandalucia.es/boja/2010/230/1',
  'ley de hacienda publica de andalucia': 'https://www.juntadeandalucia.es/boja/2010/230/1',
  'ley del gobierno de andalucía': 'https://www.boe.es/eli/es/l/2006/10/24/6/con',
  'ley del gobierno de andalucia': 'https://www.boe.es/eli/es/l/2006/10/24/6/con',
  'ley de administración de la junta de andalucía': 'https://www.boe.es/eli/es/l/2007/10/26/9/con',
  'ley de administracion de la junta de andalucia': 'https://www.boe.es/eli/es/l/2007/10/26/9/con',
  'ley de salud de andalucía': 'https://www.boe.es/eli/es/l/1998/06/15/2/con',
  'ley de salud de andalucia': 'https://www.boe.es/eli/es/l/1998/06/15/2/con',
  'ley de servicios sociales de andalucía': 'https://www.juntadeandalucia.es/boja/2016/248/1',
  'ley de servicios sociales de andalucia': 'https://www.juntadeandalucia.es/boja/2016/248/1',
  'ley de transparencia pública de andalucía': 'https://www.juntadeandalucia.es/boja/2014/124/1',
  'ley de transparencia publica de andalucia': 'https://www.juntadeandalucia.es/boja/2014/124/1',
  'ley de patrimonio histórico de andalucía': 'https://www.boe.es/eli/es/l/2007/11/26/14/con',
  'ley de patrimonio historico de andalucia': 'https://www.boe.es/eli/es/l/2007/11/26/14/con',
  'ley de gestión integrada de la calidad ambiental': 'https://www.boe.es/eli/es/l/2007/07/09/7/con',
  'ley de gestión integrada de la calidad ambiental de andalucía': 'https://www.boe.es/eli/es/l/2007/07/09/7/con',
  'ley de coordinación de policías locales de andalucía': 'https://www.boe.es/eli/es/l/2001/12/11/13/con',
  'ley de coordinacion de policias locales de andalucia': 'https://www.boe.es/eli/es/l/2001/12/11/13/con',
  'ley de espectáculos públicos de andalucía': 'https://www.boe.es/eli/es/l/2003/05/12/13/con',
  'ley de espectaculos publicos de andalucia': 'https://www.boe.es/eli/es/l/2003/05/12/13/con',
  'ley de bienes de las entidades locales de andalucía': 'https://www.juntadeandalucia.es/boja/1999/138/1',
  'ley de turismo de andalucía': 'https://www.boe.es/eli/es/l/2011/12/23/13/con',
  'ley de turismo de andalucia': 'https://www.boe.es/eli/es/l/2011/12/23/13/con',
  'ley de aguas de andalucía': 'https://www.juntadeandalucia.es/boja/2010/145/1',
  'ley de aguas de andalucia': 'https://www.juntadeandalucia.es/boja/2010/145/1',
};

// ============================================================
// NUMERIC REFERENCE → URL MAPPING (e.g., "Ley 39/2015", "RD 2568/1986")
// These match patterns like "Ley X/YYYY" in any casing
// ============================================================
const numericRefMapping: Record<string, string> = {
  // National
  'ley 39/2015': 'https://www.boe.es/eli/es/l/2015/10/01/39/con',
  'ley 40/2015': 'https://www.boe.es/eli/es/l/2015/10/01/40/con',
  'ley 30/1992': 'https://www.boe.es/eli/es/l/1992/11/26/30/con',
  'ley 7/1985': 'https://www.boe.es/eli/es/l/1985/04/02/7/con',
  'ley 27/2013': 'https://www.boe.es/eli/es/l/2013/12/27/27/con',
  'ley 58/2003': 'https://www.boe.es/eli/es/l/2003/12/17/58/con',
  'ley 47/2003': 'https://www.boe.es/eli/es/l/2003/11/26/47/con',
  'ley 9/2017': 'https://www.boe.es/eli/es/l/2017/11/08/9/con',
  'ley 13/1995': 'https://www.boe.es/eli/es/l/1995/05/18/13/con',
  'ley 3/2017': 'https://www.boe.es/eli/es/l/2017/06/27/3/con',
  'ley 53/1984': 'https://www.boe.es/eli/es/l/1984/12/26/53/con',
  'ley 30/1984': 'https://www.boe.es/eli/es/l/1984/08/02/30/con',
  'ley 22/1988': 'https://www.boe.es/eli/es/l/1988/07/28/22/con',
  'ley 33/2003': 'https://www.boe.es/eli/es/l/2003/11/03/33/con',
  'ley 43/2003': 'https://www.boe.es/eli/es/l/2003/11/21/43/con',
  'ley 16/1985': 'https://www.boe.es/eli/es/l/1985/06/25/16/con',
  'ley 21/2013': 'https://www.boe.es/eli/es/l/2013/12/09/21/con',
  'ley 7/2022': 'https://www.boe.es/eli/es/l/2022/04/08/7/con',
  'ley 19/2013': 'https://www.boe.es/eli/es/l/2013/12/09/19/con',
  'ley 11/2007': 'https://www.boe.es/eli/es/l/2007/06/22/11/con',
  'ley 38/2003': 'https://www.boe.es/eli/es/l/2003/11/17/38/con',
  'ley 29/1998': 'https://www.boe.es/eli/es/l/1998/07/13/29/con',
  'ley 1/2000': 'https://www.boe.es/eli/es/l/2000/01/07/1/con',
  'ley 36/2011': 'https://www.boe.es/eli/es/l/2011/10/10/36/con',
  'ley 39/2006': 'https://www.boe.es/eli/es/l/2006/12/14/39/con',
  'ley 14/1986': 'https://www.boe.es/eli/es/l/1986/04/25/14/con',
  'ley 62/2003': 'https://www.boe.es/eli/es/l/2003/12/23/62/con',
  'ley 29/1994': 'https://www.boe.es/eli/es/l/1994/11/24/29/con',
  'ley 49/1960': 'https://www.boe.es/eli/es/l/1960/07/21/49/con',
  'ley 26/2007': 'https://www.boe.es/eli/es/l/2007/10/23/26/con',

  // Leyes Orgánicas
  'lo 2/2012': 'https://www.boe.es/eli/es/lo/2012/04/27/2/con',
  'lo 8/1980': 'https://www.boe.es/eli/es/lo/1980/09/22/8/con',
  'lo 4/2015': 'https://www.boe.es/eli/es/lo/2015/03/30/4/con',
  'lo 2/1986': 'https://www.boe.es/eli/es/lo/1986/03/13/2/con',
  'lo 5/2005': 'https://www.boe.es/eli/es/lo/2005/11/17/5/con',
  'lo 3/2018': 'https://www.boe.es/eli/es/lo/2018/12/05/3/con',
  'lo 10/1995': 'https://www.boe.es/eli/es/lo/1995/11/23/10/con',
  'lo 6/1985': 'https://www.boe.es/eli/es/lo/1985/07/01/6/con',
  'lo 2/1979': 'https://www.boe.es/eli/es/lo/1979/10/03/2/con',
  'lo 2/2007': 'https://www.boe.es/eli/es/lo/2007/03/19/2/con',

  // Reales Decretos Legislativos
  'rdleg 781/1986': 'https://www.boe.es/eli/es/rdlg/1986/04/18/781/con',
  'rdleg 2/2004': 'https://www.boe.es/eli/es/rdlg/2004/03/05/2/con',
  'rdleg 5/2015': 'https://www.boe.es/eli/es/rdlg/2015/10/30/5/con',
  'rdleg 3/2011': 'https://www.boe.es/eli/es/rdlg/2011/11/14/3/con',
  'rdleg 6/2015': 'https://www.boe.es/eli/es/rdlg/2015/10/30/6/con',
  'rdleg 7/2015': 'https://www.boe.es/eli/es/rdlg/2015/10/30/7/con',
  'rdleg 1/2001': 'https://www.boe.es/eli/es/rdlg/2001/07/20/1/con',
  'rdleg 1/2016': 'https://www.boe.es/eli/es/rdlg/2016/12/16/1/con',
  'rdleg 8/2015': 'https://www.boe.es/eli/es/rdlg/2015/10/30/8/con',
  'rdleg 1/2013': 'https://www.boe.es/eli/es/rdlg/2013/11/29/1/con',
  'rdleg 2/2015': 'https://www.boe.es/eli/es/rdlg/2015/10/23/2/con',
  'rdleg 5/2000': 'https://www.boe.es/eli/es/rdlg/2000/08/04/5/con',

  // Reales Decretos
  'rd 2568/1986': 'https://www.boe.es/eli/es/rd/1986/11/28/2568/con',
  'rd 1372/1986': 'https://www.boe.es/eli/es/rd/1986/06/13/1372/con',
  'rd 1690/1986': 'https://www.boe.es/eli/es/rd/1986/07/11/1690/con',
  'rd 1098/2001': 'https://www.boe.es/eli/es/rd/2001/10/12/1098/con',
  'rd 364/1995': 'https://www.boe.es/eli/es/rd/1995/03/10/364/con',
  'rd 500/1990': 'https://www.boe.es/eli/es/rd/1990/04/20/500/con',
  'rd 887/2006': 'https://www.boe.es/eli/es/rd/2006/07/21/887/con',
  'rd 1346/1978': 'https://www.boe.es/eli/es/rd/1978/06/23/1346/con',
  'rd 1428/2003': 'https://www.boe.es/eli/es/rd/2003/11/21/1428/con',

  // Órdenes
  'orden eha/4041/2004': 'https://www.boe.es/eli/es/o/2004/11/23/eha4041/con',
  'orden hap/1781/2013': 'https://www.boe.es/eli/es/o/2013/09/20/hap1781/con',

  // EU
  'directiva 2006/123': 'https://eur-lex.europa.eu/eli/dir/2006/123/oj/spa',
  'reglamento 910/2014': 'https://eur-lex.europa.eu/eli/reg/2014/910/oj/spa',

  // Andalucía
  'ley 5/2010': 'https://www.boe.es/eli/es/l/2010/06/11/5/con',
  'ley 7/2002': 'https://www.boe.es/eli/es/l/2002/12/17/7/con',
  'ley 6/2006': 'https://www.boe.es/eli/es/l/2006/10/24/6/con',
  'ley 9/2007': 'https://www.boe.es/eli/es/l/2007/10/26/9/con',
  'ley 2/1998': 'https://www.boe.es/eli/es/l/1998/06/15/2/con',
  'ley 14/2007': 'https://www.boe.es/eli/es/l/2007/11/26/14/con',
  'ley 7/2007': 'https://www.boe.es/eli/es/l/2007/07/09/7/con',
  'ley 13/2001': 'https://www.boe.es/eli/es/l/2001/12/11/13/con',
  'ley 2/2003': 'https://www.boe.es/eli/es/l/2003/05/12/13/con',
  'ley 13/2011': 'https://www.boe.es/eli/es/l/2011/12/23/13/con',

  // Extremadura
  'ley 11/2018': 'https://doe.juntaex.es/pdfs/doe/2018/1230o/18010007.pdf',
  'ley 13/2015': 'https://doe.juntaex.es/pdfs/doe/2015/760o/15010003.pdf',
  'ley 17/2010': 'https://doe.juntaex.es/pdfs/doe/2010/630o/10010009.pdf',
  'ley 4/2013': 'https://doe.juntaex.es/pdfs/doe/2013/480o/13010003.pdf',
};

// ============================================================
// REGEX PATTERNS
// ============================================================

// Build abbreviation regex: CASE-SENSITIVE, only matches uppercase
// Sort by length descending to match longer abbreviations first (e.g., LECrim before LEC)
const abbreviationKeys = Object.keys(abbreviationMapping).sort((a, b) => b.length - a.length);
const abbreviationRegex = new RegExp(
  `\\b(?:${abbreviationKeys.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`,
  'g' // NO 'i' flag — case-sensitive
);

// Build full name regex: case-INSENSITIVE (safe because they are long phrases)
// Sort by length descending to match longer names first
const fullNameKeys = Object.keys(fullNameMapping).sort((a, b) => b.length - a.length);
const fullNameRegex = new RegExp(
  `(?:${fullNameKeys.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
  'gi' // case-insensitive
);

// Other legal patterns
const legalPatterns = {
  // BOE references: BOE-A-2024-1234, BOE núm. 123
  boe: /\bBOE(?:-[A-Z]-\d{4}-\d+|(?:\s+)?(?:n(?:úm(?:ero)?|º)?\.?\s*\d+))/gi,
  
  // Laws: Ley 7/1985, Ley Orgánica 3/2018, Real Decreto 2568/1986, etc.
  laws: /\b(?:Ley\s+Orgánica|Ley|Real\s+Decreto(?:-[Ll]ey)?(?:\s+[Ll]egislativo)?|Decreto(?:-[Ll]ey)?|Orden(?:\s+\w+\/\d+)?|Resolución)\s+\d+\/\d{4}/gi,
  
  // Constitutional Court: STC 123/2020
  stc: /\bSTC\s+\d+\/\d{4}/gi,
  
  // Supreme Court: STS 1234/2020, STSJ
  sts: /\bSTS(?:J)?\s+\d+\/\d{4}/gi,
  
  // European regulations: Reglamento (UE) 2016/679
  eu: /\bReglamento\s*\(?(?:UE|CE)\)?\s*(?:n(?:º|úm\.?)?\s*)?\d+\/\d{4}/gi,
  
  // Directives: Directiva 2006/123/CE
  directive: /\bDirectiva\s+\d+\/\d+\/(?:UE|CE)/gi,
  
  // Articles: artículo 123, art. 45
  articles: /\b(?:art(?:ículo)?\.?\s+\d+(?:\.\d+)?(?:\s*(?:y|,)\s*\d+(?:\.\d+)?)*)/gi,
  
  // Instrucciones and Circulares
  instrucciones: /\b(?:Instrucción|Circular)\s+\d+\/\d{4}/gi,
  
  // Órdenes ministeriales: Orden HAP/1781/2013, Orden EHA/4041/2004
  ordenes: /\bOrden\s+(?:[A-Z]{2,4}\/\d+\/\d{4})/gi,
};

// Build BOE URL from reference
function buildBoeUrl(ref: string): string {
  const boeAMatch = ref.match(/BOE-([A-Z])-(\d{4})-(\d+)/i);
  if (boeAMatch) {
    return `https://www.boe.es/buscar/doc.php?id=${ref.toUpperCase()}`;
  }
  const boeNumMatch = ref.match(/BOE\s*(?:n(?:úm(?:ero)?|º)?\.?\s*)(\d+)/i);
  if (boeNumMatch) {
    return `https://www.boe.es/buscar/boe.php?campo%5B0%5D=NUM&dato%5B0%5D=${boeNumMatch[1]}`;
  }
  return `https://www.boe.es/buscar/act.php?accion=buscar&texto=${encodeURIComponent(ref)}`;
}

// Build court decision search URL
function buildCourtUrl(ref: string, type: 'stc' | 'sts'): string {
  const match = ref.match(/(\d+)\/(\d{4})/);
  if (!match) {
    return `https://www.poderjudicial.es/search/index.jsp`;
  }
  const [, number, year] = match;
  if (type === 'stc') {
    return `https://hj.tribunalconstitucional.es/es/Resolucion/Show/${year}/${number}`;
  }
  return `https://www.poderjudicial.es/search/indexAN.jsp?org=ts&t=${encodeURIComponent(ref)}`;
}

// Build EU law URL
function buildEuUrl(ref: string): string {
  const match = ref.match(/(\d+)\/(\d{4})/);
  if (match) {
    const [, number, year] = match;
    return `https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=CELEX%3A3${year}R${number.padStart(4, '0')}`;
  }
  return `https://eur-lex.europa.eu/homepage.html?locale=es`;
}

// Helper: check if a position in text is already inside a markdown link
function isInsideLink(text: string, matchStart: number, matchEnd: number): boolean {
  // Check if this match is already wrapped in [...](...) 
  // Look for [ before the match and ]( after
  const before = text.substring(Math.max(0, matchStart - 1), matchStart);
  if (before === '[') return true;
  
  // Check if we're inside an existing markdown link
  const textBefore = text.substring(0, matchStart);
  const lastOpenBracket = textBefore.lastIndexOf('[');
  const lastCloseBracket = textBefore.lastIndexOf(']');
  if (lastOpenBracket > lastCloseBracket) {
    // We're between [ and ], likely inside a link text
    return true;
  }
  
  // Check if we're inside a URL portion ](...)
  const lastLinkStart = textBefore.lastIndexOf('](');
  if (lastLinkStart !== -1) {
    const closeParen = text.indexOf(')', lastLinkStart);
    if (closeParen >= matchEnd) return true;
  }
  
  return false;
}

/**
 * Process text and convert legal references to markdown links.
 * 
 * Processing order:
 * 1. Full law names (case-insensitive, longest first) — safe, long phrases
 * 2. Abbreviations (CASE-SENSITIVE, uppercase only) — no false positives
 * 3. Numeric references from library (case-insensitive)
 * 4. Generic legal patterns (BOE, STC, STS, EU, Directives, etc.)
 */
export function processLegalReferences(text: string): string {
  if (!text || typeof text !== 'string') return '';
  let processedText = text;
  
  // Track which ranges have been converted to avoid double-linking
  const linkedRanges: Array<{ start: number; end: number }> = [];
  
  function isRangeLinked(start: number, end: number): boolean {
    return linkedRanges.some(r => 
      (start >= r.start && start < r.end) || 
      (end > r.start && end <= r.end) ||
      (start <= r.start && end >= r.end)
    );
  }
  
  // === STEP 1: Full law names (case-insensitive, longest first) ===
  fullNameRegex.lastIndex = 0;
  processedText = processedText.replace(fullNameRegex, (match, offset) => {
    if (isInsideLink(processedText, offset, offset + match.length)) return match;
    if (isRangeLinked(offset, offset + match.length)) return match;
    
    const key = match.toLowerCase();
    const url = fullNameMapping[key];
    if (url) {
      const replacement = `[${match}](${url})`;
      // Adjust linked ranges for future checks
      linkedRanges.push({ start: offset, end: offset + replacement.length });
      return replacement;
    }
    return match;
  });
  
  // === STEP 2: Abbreviations (CASE-SENSITIVE, uppercase only) ===
  abbreviationRegex.lastIndex = 0;
  processedText = processedText.replace(abbreviationRegex, (match, offset) => {
    if (isInsideLink(processedText, offset, offset + match.length)) return match;
    
    const url = abbreviationMapping[match]; // exact case match
    if (url) {
      return `[${match}](${url})`;
    }
    return match;
  });
  
  // === STEP 3: Numeric references from library ===
  // Match patterns like "Ley 39/2015", "LO 2/2007", "RDLeg 5/2015", "RD 2568/1986", "Orden EHA/4041/2004"
  const numericPattern = /\b(?:Ley\s+Orgánica|Ley|LO|Real\s+Decreto(?:\s+Legislativo)?|RDLeg|RD|Decreto|Orden)\s+(?:[A-Z]{2,4}\/)?(\d+\/\d{4})/gi;
  processedText = processedText.replace(numericPattern, (match, _numYear, offset) => {
    if (processedText.includes(`[${match}]`)) return match;
    if (isInsideLink(processedText, offset, offset + match.length)) return match;
    
    // Normalize the key for lookup
    const key = match.toLowerCase()
      .replace(/\s+orgánica/, '')
      .replace(/real\s+decreto\s+legislativo/, 'rdleg')
      .replace(/real\s+decreto/, 'rd')
      .replace(/decreto/, 'decreto')
      .trim();
    
    const url = numericRefMapping[key];
    if (url) {
      return `[${match}](${url})`;
    }
    
    // Fallback: search BOE for unrecognized numeric refs
    return match; // Don't link if not in library
  });
  
  // === STEP 4: BOE references ===
  processedText = processedText.replace(legalPatterns.boe, (match, offset) => {
    if (processedText.includes(`[${match}]`)) return match;
    if (isInsideLink(processedText, offset, offset + match.length)) return match;
    const url = buildBoeUrl(match);
    return `[${match}](${url})`;
  });
  
  // === STEP 5: Generic law references (Ley X/YYYY not in library) ===
  processedText = processedText.replace(legalPatterns.laws, (match, offset) => {
    if (processedText.includes(`[${match}]`)) return match;
    if (isInsideLink(processedText, offset, offset + match.length)) return match;
    // Only link if we find it in numericRefMapping
    const key = match.toLowerCase()
      .replace(/\s+orgánica/, '')
      .replace(/real\s+decreto\s+legislativo/, 'rdleg')
      .replace(/real\s+decreto(?:-ley)?/, 'rd')
      .trim();
    const url = numericRefMapping[key];
    if (url) {
      return `[${match}](${url})`;
    }
    return match; // Don't link if not in library
  });
  
  // === STEP 6: STC (Constitutional Court) ===
  processedText = processedText.replace(legalPatterns.stc, (match, offset) => {
    if (processedText.includes(`[${match}]`)) return match;
    if (isInsideLink(processedText, offset, offset + match.length)) return match;
    const url = buildCourtUrl(match, 'stc');
    return `[${match}](${url})`;
  });
  
  // === STEP 7: STS (Supreme Court) ===
  processedText = processedText.replace(legalPatterns.sts, (match, offset) => {
    if (processedText.includes(`[${match}]`)) return match;
    if (isInsideLink(processedText, offset, offset + match.length)) return match;
    const url = buildCourtUrl(match, 'sts');
    return `[${match}](${url})`;
  });
  
  // === STEP 8: EU regulations ===
  processedText = processedText.replace(legalPatterns.eu, (match, offset) => {
    if (processedText.includes(`[${match}]`)) return match;
    if (isInsideLink(processedText, offset, offset + match.length)) return match;
    const url = buildEuUrl(match);
    return `[${match}](${url})`;
  });
  
  // === STEP 9: EU directives ===
  processedText = processedText.replace(legalPatterns.directive, (match, offset) => {
    if (processedText.includes(`[${match}]`)) return match;
    if (isInsideLink(processedText, offset, offset + match.length)) return match;
    const url = `https://eur-lex.europa.eu/search.html?scope=EURLEX&text=${encodeURIComponent(match)}&lang=es&type=quick`;
    return `[${match}](${url})`;
  });
  
  // === STEP 10: Instrucciones and Circulares ===
  processedText = processedText.replace(legalPatterns.instrucciones, (match, offset) => {
    if (processedText.includes(`[${match}]`)) return match;
    if (isInsideLink(processedText, offset, offset + match.length)) return match;
    const url = `https://www.boe.es/buscar/act.php?accion=buscar&texto=${encodeURIComponent(match)}`;
    return `[${match}](${url})`;
  });
  
  // === STEP 11: Órdenes ministeriales ===
  processedText = processedText.replace(legalPatterns.ordenes, (match, offset) => {
    if (processedText.includes(`[${match}]`)) return match;
    if (isInsideLink(processedText, offset, offset + match.length)) return match;
    const key = match.toLowerCase();
    const url = numericRefMapping[key];
    if (url) {
      return `[${match}](${url})`;
    }
    return match; // Don't link if not in library
  });
  
  return processedText;
}

/**
 * Check if text contains any legal references
 */
export function containsLegalReferences(text: string): boolean {
  // Check abbreviations
  abbreviationRegex.lastIndex = 0;
  if (abbreviationRegex.test(text)) return true;
  
  // Check full names
  fullNameRegex.lastIndex = 0;
  if (fullNameRegex.test(text)) return true;
  
  // Check other patterns
  return Object.values(legalPatterns).some(pattern => {
    pattern.lastIndex = 0;
    return pattern.test(text);
  });
}

/**
 * Get list of all supported law abbreviations
 */
export function getSupportedAbbreviations(): string[] {
  return Object.keys(abbreviationMapping);
}
